import { isPlatformBrowser } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule, PLATFORM_ID } from '@angular/core';
import { TransferState } from '@angular/platform-browser';
import { ErrorTracking, PerformanceTracking } from '@freelancer/tracking';
import { EffectsModule } from '@ngrx/effects';
import { MetaReducer, META_REDUCERS, StoreModule } from '@ngrx/store';
import { TypedAction } from './actions';
import { ApiHttp } from './api-http.service';
import { BackendModule } from './backend';
import { Datastore } from './datastore';
import { DATASTORE_CONFIG, HTTP_ADAPTER } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';
import { errorHandlerReducerFactory } from './error-handler.reducer';
import { REQUEST_DATA_INITIAL_CONFIG } from './request-data';
import { RequestDataModule } from './request-data/request-data.module';
import { RequestStatusHandler } from './request-status-handler.service';
import { SearchManager, SearchModule } from './search';
import { sessionStorageSyncFactory } from './session-storage-sync.reducer';
import { stateTransferReducerFactory } from './state-transfer.reducer';
import { storeFreezeReducerFactory } from './store-freeze.reducer';
import { WebSocketService } from './websocket';

export function getMetaReducers(
  errorTracking: ErrorTracking,
  performanceTracking: PerformanceTracking,
  config: DatastoreConfig,
  platformId: Object,
  transferState: TransferState,
): ReadonlyArray<MetaReducer<any, TypedAction>> {
  const metaReducers: MetaReducer<any, TypedAction>[] = [
    errorHandlerReducerFactory(errorTracking),
    stateTransferReducerFactory(transferState, platformId),
  ];
  // Store session cache is disabled in local dev
  if (
    isPlatformBrowser(platformId) &&
    window.location.hostname !== 'localhost'
  ) {
    metaReducers.push(sessionStorageSyncFactory(performanceTracking));
  }
  if (config.enableStoreFreeze) {
    metaReducers.push(storeFreezeReducerFactory(performanceTracking));
  }
  return metaReducers;
}

@NgModule({
  imports: [
    HttpClientModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    RequestDataModule.initialize(),
    SearchModule.forRoot(),
    BackendModule.forRoot(),
  ],
  providers: [ApiHttp, WebSocketService, RequestStatusHandler],
})
export class DatastoreModule {
  static initialize(config: DatastoreConfig): ModuleWithProviders {
    return {
      ngModule: DatastoreModule,
      providers: [
        Datastore,
        {
          provide: DATASTORE_CONFIG,
          useValue: config,
        },
        {
          provide: META_REDUCERS,
          deps: [
            ErrorTracking,
            PerformanceTracking,
            DATASTORE_CONFIG,
            PLATFORM_ID,
            TransferState,
          ],
          useFactory: getMetaReducers,
        },
        {
          provide: HTTP_ADAPTER,
          useClass: config.httpAdapter,
        },
        {
          provide: REQUEST_DATA_INITIAL_CONFIG,
          useValue: config.requestData,
        },
        SearchManager,
      ],
    };
  }
}
